import * as voicifyApi from '../api';
import IResult from '../models/result/IResult';
import AppliedApplicationTemplateModel from '../models/templating/api/AppliedApplicationTemplateModel';
import TemplateFormFieldModel from '../models/templating/api/TemplateFormFieldModel';
import TemplateFormFieldValueModel from '../models/templating/api/TemplateFormFieldValueModel';
import TemplateFormSectionModel from '../models/templating/api/TemplateFormSectionModel';
import UpdateAppliedApplicationTemplateFormRequest from '../models/templating/api/UpdateAppliedApplicationTemplateFormRequest';

export interface ModuleFieldLocation {
    appliedTemplate: AppliedApplicationTemplateModel;
    templateFormFieldId: string;
    // Set when the field lives in a dynamic section; its values are keyed by this name, not the field ID
    dynamicFormFieldName?: string;
    value: string | null;
}

const sameVariable = (a?: string, b?: string) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const sectionFields = (sections?: TemplateFormSectionModel[]): TemplateFormFieldModel[] =>
    (sections || []).reduce((all, section: any) => all.concat(section?.templateFormFields ?? section?.dynamicFormFields ?? []), [] as TemplateFormFieldModel[]);

// Dynamic fields are matched by name (like sectionUtils.getFieldValue), static fields by ID
const isRecordFor = (record: TemplateFormFieldValueModel, location: { templateFormFieldId: string; dynamicFormFieldName?: string }) =>
    location.dynamicFormFieldName
        ? record.dynamicFormFieldName === location.dynamicFormFieldName
        : record.templateFormFieldId === location.templateFormFieldId;

// Finds the module field for a variable among an app's applied templates, in static and dynamic sections.
// A value equal to the variable name is the shared app's "set per location" sentinel, so that field doesn't count.
export const findModuleField = async (applicationId: string, associatedVariable: string): Promise<IResult<ModuleFieldLocation | null>> => {
    const templatesResult = await voicifyApi.getAppliedApplicationTemplatesForApp(applicationId);
    if (templatesResult.resultType !== 'Ok') {
        return { ...templatesResult, data: null } as IResult<ModuleFieldLocation | null>;
    }

    for (const appliedTemplate of templatesResult.data || []) {
        const candidates = [
            ...sectionFields(appliedTemplate.templateForm?.templateFormSections).map(field => ({ field, dynamicFormFieldName: undefined as string | undefined })),
            ...sectionFields(appliedTemplate.dynamicFormSections).map(field => ({ field, dynamicFormFieldName: field.name || undefined }))
        ];
        const match = candidates.find(({ field, dynamicFormFieldName }) => (field.id || dynamicFormFieldName) && sameVariable(field.associatedVariable, associatedVariable));
        if (!match) continue;

        const location = { templateFormFieldId: match.field.id, dynamicFormFieldName: match.dynamicFormFieldName };
        const record = (appliedTemplate.templateFormFieldValues || []).find(v => isRecordFor(v, location));
        const value = record?.value ?? match.field.defaultValue ?? null;
        if (sameVariable(value, associatedVariable)) continue;

        return { resultType: 'Ok', data: { appliedTemplate, ...location, value }, errors: [] } as IResult<ModuleFieldLocation>;
    }
    return { resultType: 'Ok', data: null, errors: [] } as IResult<ModuleFieldLocation | null>;
};

// Schema defaults for static fields with no value record yet, as the module editor's finish adds them, so the
// sync never pushes a blank for a newly added field. Per-location sentinel records are left untouched.
const withSchemaDefaults = (template: AppliedApplicationTemplateModel, values: TemplateFormFieldValueModel[]): TemplateFormFieldValueModel[] => {
    const defaults = sectionFields(template.templateForm?.templateFormSections)
        .filter(field => field.id && field.defaultValue != null && field.defaultValue !== '' && !values.some(v => v.templateFormFieldId === field.id))
        .map(field => ({
            templateFormFieldId: field.id,
            appliedApplicationTemplateId: template.id,
            value: field.defaultValue,
            fieldType: field.fieldType,
            label: field.label,
            title: field.title,
            tip: field.tip,
            placeholder: field.placeholder,
            defaultValue: field.defaultValue,
            associatedVariable: field.associatedVariable
        } as TemplateFormFieldValueModel));
    return defaults.length ? [...values, ...defaults] : values;
};

/**
 * Sets one module field and finishes the form, the same way the module editor's finish does:
 * saves the values and queues a sync. Nothing is published.
 * Returns the sync ID when one was queued.
 */
export const updateModuleFieldAndFinalize = async (applicationId: string, location: ModuleFieldLocation, value: string): Promise<IResult<string | null>> => {
    const { appliedTemplate } = location;
    // Start from the latest saved values so edits made elsewhere since loading aren't overwritten.
    // Without them the full-form write would revert those edits, so stop instead.
    const latest = await voicifyApi.findAppliedApplicationTemplate(appliedTemplate.id);
    if (latest.resultType !== 'Ok' || !latest.data) {
        return { resultType: latest.resultType === 'Ok' ? 'Unexpected' : latest.resultType, errors: latest.errors?.length ? latest.errors : ["Couldn't load the latest module values"], data: null } as IResult<string | null>;
    }
    const template: AppliedApplicationTemplateModel = { ...appliedTemplate, ...latest.data };

    const existingValues = template.templateFormFieldValues || [];
    const hasRecord = existingValues.some(v => isRecordFor(v, location));
    const field = [...sectionFields(template.templateForm?.templateFormSections), ...sectionFields(template.dynamicFormSections)]
        .find(f => location.dynamicFormFieldName ? f.name === location.dynamicFormFieldName : f.id === location.templateFormFieldId);
    const fieldValues: TemplateFormFieldValueModel[] = hasRecord
        ? existingValues.map(v => isRecordFor(v, location) ? { ...v, value } : v)
        : [...existingValues, {
            templateFormFieldId: location.templateFormFieldId,
            dynamicFormFieldName: location.dynamicFormFieldName,
            appliedApplicationTemplateId: template.id,
            value,
            fieldType: field?.fieldType,
            label: field?.label,
            title: field?.title,
            associatedVariable: field?.associatedVariable
        }];

    const request: UpdateAppliedApplicationTemplateFormRequest = {
        ...template,
        templateFormFieldValues: withSchemaDefaults(template, fieldValues),
        // The saved dynamic sections, as the bulk sync sends them; otherwise the named sections, as the module editor sends them
        dynamicFormSections: template.dynamicFormSections?.length
            ? template.dynamicFormSections
            : (template.templateForm?.templateFormSections || []).filter(s => s?.name)
    };

    const result: IResult<any> = template.templateForm?.templateConfiguration?.finalizeFormUrl
        ? await voicifyApi.finalizeDynamicForm(template.id, { applicationId, data: request })
        : await voicifyApi.queueUpdateApplicationFromTemplateFormsSync(applicationId, template.id, request);

    if (result.resultType !== 'Ok') {
        return { ...result, data: null } as IResult<string | null>;
    }
    if (result.data?.errors?.length) {
        return { resultType: 'Invalid', errors: result.data.errors, data: null } as IResult<string | null>;
    }
    return { resultType: 'Ok', errors: [], data: result.data?.appliedTemplateSyncId || result.data?.id || null } as IResult<string | null>;
};
